import { type Identifier, useTimeout, useTranslate } from "ra-core";
import { useIsMobile } from "@/hooks/use-mobile";

import { TaskListFilter } from "./TasksListFilter";
import {
  ALL_SECTIONS,
  type TaskSection,
  useTasksByDueDate,
} from "./useTasksByDueDate";
import { isBeforeFriday } from "./tasksPredicate";

export const TasksListByDueDate = ({
  filterByContact,
  emptyPlaceholder,
  pendingPlaceholder,
  sections = ALL_SECTIONS,
}: {
  filterByContact?: Identifier;
  emptyPlaceholder?: React.ReactNode;
  pendingPlaceholder?: React.ReactNode;
  /** which due-date groups to show, e.g. only overdue and today on «Сегодня» */
  sections?: TaskSection[];
}) => {
  const isMobile = useIsMobile();
  const translate = useTranslate();
  const { tasksBySection, isPending } = useTasksByDueDate(filterByContact);
  const showContact = filterByContact == null;

  const oneSecondHasPassed = useTimeout(1000);

  if (isPending && oneSecondHasPassed) {
    return pendingPlaceholder ?? null;
  }

  if (isPending) {
    return null;
  }

  const shows = (section: TaskSection) => sections.includes(section);

  if (!sections.some((section) => tasksBySection[section].length)) {
    return emptyPlaceholder ?? null;
  }

  return (
    <div className="flex flex-col gap-4">
      {shows("overdue") && (
        <TaskListFilter
          tasks={tasksBySection.overdue}
          title={translate("resources.tasks.filters.overdue")}
          overdue
          showContact={showContact}
          isMobile={isMobile}
        />
      )}
      {shows("today") && (
        <TaskListFilter
          tasks={tasksBySection.today}
          title={translate("resources.tasks.filters.today")}
          showContact={showContact}
          isMobile={isMobile}
        />
      )}
      {shows("tomorrow") && (
        <TaskListFilter
          tasks={tasksBySection.tomorrow}
          title={translate("resources.tasks.filters.tomorrow")}
          showContact={showContact}
          isMobile={isMobile}
        />
      )}
      {shows("this_week") &&
        (!filterByContact || (filterByContact && isBeforeFriday())) && (
          <TaskListFilter
            tasks={tasksBySection.this_week}
            title={translate("resources.tasks.filters.this_week")}
            showContact={showContact}
            isMobile={isMobile}
          />
        )}
      {shows("later") && (
        <TaskListFilter
          tasks={tasksBySection.later}
          title={translate("resources.tasks.filters.later")}
          showContact={showContact}
          isMobile={isMobile}
        />
      )}
    </div>
  );
};
